import React, { useMemo } from 'react';
import { getMessage } from '@tcc/i18n';
import { Collapse } from '../../../../components/Collapse';
import styles from '../../../../components/cards/cardAndPreview/cardAndPreview.css';
import { localizeUnits } from '../../../../utils/processUnits';
import { PlantingProducts } from '../../../../types';
import { isoToDateString, toDateString } from '../../../../utils/date';
import { useAppSelector } from '../../../../utils';
import { getLocale, getPlantingArdDataFeatureSelector } from '../../../../selectors';
import { PlantingProductsGroup } from '../types';
import { HybridMetrics } from './HybridMetrics';

interface HybridsListProps {
  seedProducts: PlantingProducts;

}

const getSeedProductsIndexed = 
  (seedProducts: PlantingProducts, locale: string, isArdDataEnabled: boolean): Record<string, PlantingProductsGroup> =>
    seedProducts.reduce((accum, {id, plantedArea, unit, name, date}) => {
      const newIndex = {...accum}
      if(typeof accum[id] === 'undefined') {
        newIndex[id] = {
          id,
          name,
          events: []
        }
      }

      let plantingDate: string;
      if (isArdDataEnabled) {
        plantingDate = toDateString(date, locale)
      } else {
        plantingDate = isoToDateString(date, locale);
      }
      
      const locPlantedArea =
        plantedArea &&
        localizeUnits(
          plantedArea,
          { key: unit, localeGroup: 'area' },
        );

      newIndex[id].events.push({
        plantingDate,
        locPlantedArea,
      });

      return newIndex;
    }, {}
  );

export function HybridsList({ seedProducts }: HybridsListProps) {
  const state = useAppSelector((state) => state);
  const locale = getLocale(state);
  const isArdDataEnabled = getPlantingArdDataFeatureSelector(state)

  const seedProductsGrouped = useMemo(() => 
    Object.values(getSeedProductsIndexed(seedProducts, locale, isArdDataEnabled)), [locale, seedProducts, isArdDataEnabled]);

  if (!seedProducts.length) {
    return null;
  }

  return (
    <div className={styles.hybridsList} data-test-id="hybrids-list-container">
      <h3 data-test-id="hybrids-title">
        {seedProducts.length > 1
          ? getMessage('layer.hybrids')
          : getMessage('layer.hybrid')}
      </h3>
      <Collapse
        collapseText={getMessage('collapse')}
        expandText={getMessage('card.all.hybrids')}
        testId='show-all-hybrids'
      >
        {
          seedProductsGrouped.map(({id, name, events}) => (
            <HybridMetrics key={`hybrid-metrics-${id}`} id={id} name={name} events={events} />
          ))
        }
      </Collapse>
    </div>
  );
}
